import { ApiService } from "./api.service";
import { IResponse } from "../models/response";
import {
  useQuery,
  useMutation,
  MutationFunction,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CreateSubTaskModel,
  CreateVKATemplateModel,
  MicrobiologicalOptionsModel,
  SubTaskModel,
  SubTaskViewModel,
  TemplateOptionsViewModel,
} from "../models/sub-tasks/sub-tasks.model";
import { AxiosError } from "axios";
import { MicroSettings } from "../models/micro-settings.model";

/**
 * Hook to fetch a list of sub-tasks for a given task (with pagination).
 *
 * @param {string} [taskId] - Task ID.
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=10] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @returns {Object} React Query object with sub-tasks list and a `refetch` function.
 */
export const useFetchSubTasks = (
  taskId?: string,
  page = 1,
  itemsPerPage = 10,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = [
    "sub-tasks",
    taskId,
    page,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];

  const { refetch, ...queryResult } = useQuery<
    IResponse<SubTaskModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params = {
      page: page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const subTasksData = await ApiService.get(`/tasks/${taskId}/subtasks`, {
      params,
    });
    return subTasksData.data;
  });

  return { ...queryResult, refetch };
};

/**
 * Hook to fetch all sub-tasks for a given task without pagination.
 *
 * @param {string} [taskId] - Task ID.
 * @returns {Object} React Query object with sub-tasks and a `refetch` function.
 */
export const useFetchSubTasksWithoutPagination = (taskId?: string) => {
  const queryKey = ["sub-tasks", taskId];
  const { refetch, ...queryResult } = useQuery<
    IResponse<SubTaskModel[]>,
    AxiosError
  >(queryKey, () =>
    ApiService.get(`/tasks/${taskId}/subtasks?pagination=0`)
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to create a new sub-task under a specific task.
 *
 * @param {string} [taskId] - Task ID.
 * @returns {Object} Mutation object to create sub-task.
 */
export const useCreateSubTask = (taskId?: string) => {
  const createSubTask: MutationFunction<
    IResponse<CreateSubTaskModel>,
    FormData
  > = async (formData: any) => {
    // Make the API request using the formData
    const response = await ApiService.postFormData(
      `/tasks/${taskId}/subtasks`,
      formData
    );
    return response.data;
  };
  return useMutation(createSubTask);
};

/**
 * Hook to fetch the details of a specific sub-task.
 *
 * @param {string} [id] - Sub-task ID.
 * @returns {Object} React Query object with sub-task details and a `refetch` function.
 */
export const useGetSubTaskDetails = (id?: string) => {
  const queryKey = ["sub-task", id];
  const { refetch, ...queryResult } = useQuery<
    IResponse<SubTaskViewModel>,
    AxiosError
  >(queryKey, async () => {
    const subTaskData = await ApiService.get(`/subtasks/${id}`);
    return subTaskData;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch the per-attribute traffic-light status for a laboratory VKA sub-task of a
 * VDI plant. Returns `{ type: '6044'|'2035'|null, statuses: { attribute: 'red'|'yellow'|'green'|'none' } }`.
 *
 * Only enabled for VDI plants (the backend returns an empty statuses map otherwise).
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @param {boolean} [enabled=true] - Whether to run the query (pass `isVdiPlant`).
 * @returns {Object} React Query object with the status map and a `refetch` function.
 */
export const useFetchLabParameterStatus = (
  subTaskId?: string,
  enabled = true
) => {
  const queryKey = ["lab-parameter-status", subTaskId];
  const { refetch, ...queryResult } = useQuery<
    { type: string | null; statuses: Record<string, string> },
    AxiosError
  >(
    queryKey,
    async () => {
      const response = await ApiService.get(
        `/subtasks/${subTaskId}/lab-parameter-status`
      );
      return response?.data;
    },
    { enabled: enabled && !!subTaskId }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch available template options.
 *
 * @returns {Object} React Query object with template options and a `refetch` function.
 */
export const useGetTemplateOptions = () => {
  const queryKey = ["template-options"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<TemplateOptionsViewModel>,
    AxiosError
  >(queryKey, async () => {
    const templateOptions = await ApiService.get(`/template-options`);
    return templateOptions;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch child tasks with their subtasks for an IW parent task.
 *
 * @param {string} [taskId] - IW Parent Task ID.
 * @returns {Object} React Query object with child tasks and their subtasks.
 */
export const useFetchIWChildTasks = (taskId?: string) => {
  const queryKey = ["iw-child-tasks", taskId];
  const { refetch, ...queryResult } = useQuery<
    IResponse<any[]>,
    AxiosError
  >(
    queryKey,
    async () => {
      const childTasksData = await ApiService.get(
        `/tasks/${taskId}/iw-child-tasks`
      );
      return childTasksData?.data;
    },
    {
      enabled: !!taskId,
      // Child subtasks are edited on their own detail pages (which don't
      // invalidate this list), so their status/color would otherwise stay
      // stale when returning here. Always refetch on remount and when the tab
      // regains focus so the plant colours reflect the latest status.
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to create an Order Handling template (offline support).
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object for creating an Order Handling template.
 */
export const useCreateOrderHandlingTemplate = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  const createOrderHandlingTemplate: MutationFunction<
    any,
    any
  > = async (postData: any) => {
    const response = await ApiService.post(
      `/subtasks/${subTaskId}/templates`,
      postData
    );
    return response.data;
  };

  const mutation = useMutation(createOrderHandlingTemplate, {
    onMutate: async (postData) => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);
      // Optimistically update to the new value
      queryClient.setQueryData(["sub-task", subTaskId], (old: any) => ({
        ...old,
        template: postData,
      }));
      // Return a context object with the snapshotted value
      return { previousData };
    },
  });

  // Set up the mutation when offline
  queryClient.setMutationDefaults(["createOrderHandlingTemplate"], {
    mutationFn: async (postData) => {
      // To avoid clashes with our optimistic update when an offline mutation continues
      await queryClient.cancelQueries(["createOrderHandlingTemplate"]);
      return createOrderHandlingTemplate(postData);
    },
  });

  return mutation;
};

/**
 * Hook to create a comment for a sub-task.
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object to create comment.
 */
export const useCreateComment = (subTaskId?: string) => {
  const createComment: MutationFunction<IResponse<any>, FormData> = async (
    formData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/comments`,
      formData
    );
    return response;
  };
  return useMutation(createComment);
};

/**
 * Hook to create a LaborVKA template for a sub-task.
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object for creating a VKA template.
 */
export const useCreateVKATemplate = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  const createVKATemplate: MutationFunction<
    any,
    CreateVKATemplateModel
  > = async (postData: any) => {
    const response = await ApiService.post(
      `/subtasks/${subTaskId}/templates`,
      postData
    );
    return response.data;
  };

  const mutation = useMutation(createVKATemplate, {
    onMutate: async (postData) => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);
      // Optimistically update to the new value
      queryClient.setQueryData(["sub-task", subTaskId], (old: any) => ({
        ...old,
        template: postData,
      }));
      // Return a context object with the snapshotted value
      return { previousData };
    },
  });

  // Set up the mutation when offline
  queryClient.setMutationDefaults(["createVKATemplate"], {
    mutationFn: async (postData) => {
      // To avoid clashes with our optimistic update when an offline mutation continues
      await queryClient.cancelQueries(["createVKATemplate"]);
      return createVKATemplate(postData);
    },
  });

  return mutation;
};


export const useCreateTemplateFormData = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  const createTemplateFormData: MutationFunction<
    any,
    any
  > = async (postData: any) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates`,
      postData
    );
    return response.data;
  };

  const mutation = useMutation(createTemplateFormData, {
    onMutate: async (postData) => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);
      // Optimistically update to the new value
      queryClient.setQueryData(["sub-task", subTaskId], (old: any) => ({
        ...old,
        template: postData,
      }));
      // Return a context object with the snapshotted value
      return { previousData };
    },
  });

  // Set up the mutation when offline
  queryClient.setMutationDefaults(["createTemplateFormData"], {
    mutationFn: async (postData) => {
      // To avoid clashes with our optimistic update when an offline mutation continues
      await queryClient.cancelQueries(["createTemplateFormData"]);
      return createTemplateFormData(postData);
    },
  });

  return mutation;
};

/**
 * Hook to update a micro-subtask template (offline support).
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object to update micro-subtask.
 */
export const useUpdateMicroSubTask = (subTaskId?: string) => {
  const queryClient = useQueryClient();

  const updateSubTask: MutationFunction<any, any> = async (postData: any) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates?_method=PATCH`,
      postData
    );
    return response.data;
  };

  const mutation = useMutation(updateSubTask, {
    onMutate: async (postData) => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);

      // Optimistically update to the new value
      queryClient.setQueryData(["sub-task", subTaskId], (old: any) => ({
        ...old,
        template: postData,
      }));

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onSuccess: async () => {
     await queryClient.invalidateQueries(["sub-task", subTaskId]);
    },
  });

  // Set up the mutation when offline
  queryClient.setMutationDefaults(["updateSubTask"], {
    mutationFn: async (postData) => {
      // To avoid clashes with our optimistic update when an offline mutation continues
      await queryClient.cancelQueries(["updateSubTask"]);
      return updateSubTask(postData);
    },
  });

  return mutation;
};

/**
 * Hook to update a sub-task template (offline support).
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object to update sub-task.
 */
export const useUpdateSubTask = (subTaskId?: string) => {
  const queryClient = useQueryClient();

  const updateSubTask: MutationFunction<any, any> = async (postData: any) => {
    const response = await ApiService.patch(
      `/subtasks/${subTaskId}/templates`,
      postData
    );
    return response.data;
  };

  const mutation = useMutation(updateSubTask, {
    onMutate: async (postData) => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);

      // Optimistically update to the new value
      queryClient.setQueryData(["sub-task", subTaskId], (old: any) => ({
        ...old,
        template: postData,
      }));

      // Return a context object with the snapshotted value
      return { previousData };
    },
  });

  // Set up the mutation when offline
  queryClient.setMutationDefaults(["updateSubTask"], {
    mutationFn: async (postData) => {
      // To avoid clashes with our optimistic update when an offline mutation continues
      await queryClient.cancelQueries(["updateSubTask"]);
      return updateSubTask(postData);
    },
  });

  return mutation;
};

/**
 * Hook to update LaborVKA template with Excel file.
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object to update LaborVKA template.
 */
export const useSetLabVKAExcelData = (subTaskId?: string) => {
  const labVKAExcelData: MutationFunction<IResponse<any>, FormData> = async (
    formData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates?_method=PATCH`,
      formData
    );
    return response;
  };
  return useMutation(labVKAExcelData);
};

/**
 * Hook to create a Besuch template.
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object to fill out Besuch template.
 */
export const useFilloutBesuchTemplate = (subTaskId?: string) => {
  const filloutBesuchTemplate: MutationFunction<
    IResponse<any>,
    FormData
  > = async (formData) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates`,
      formData
    );
    return response;
  };
  return useMutation(filloutBesuchTemplate);
};

/**
 * Hook to update an existing Besuch template.
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object to update Besuch template.
 */
export const useUpdateBesuchTemplate = (subTaskId?: string) => {
  const updateBesuchTemplate: MutationFunction<
    IResponse<any>,
    FormData
  > = async (formData) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates?_method=PATCH`,
      formData
    );
    return response;
  };
  return useMutation(updateBesuchTemplate);
};

/**
 * Hook to update Maintenance template.
 *
 * @param {number} subTaskId - Sub-task ID.
 * @returns {Object} Mutation object to update Maintenance template.
 */
export const useUpdateMaintenanceTemplate = (subTaskId: number) => {
  const updateMaintenanceTemplate: MutationFunction<IResponse<any>, FormData> = async (
    formData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates?_method=PATCH`,
      formData
    );
    return response;
  };
  return useMutation(updateMaintenanceTemplate);
};

/**
 * Hook to fetch resolved sub-tasks list for a plant (completed subtasks).
 *
 * @param {string} [plantId] - Plant ID.
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=10] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @returns {Object} React Query object with resolved sub-tasks list and `refetch`.
 */
export const useFetchResolvedSubTasks = (
  plantId?: string,
  page = 1,
  itemsPerPage = 10,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>,
  enabled = true
) => {
  const queryKey = [
    "resolved",
    "sub-tasks",
    plantId,
    page,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];
  // TODO: add model
  const { refetch, ...queryResult } = useQuery<IResponse<any[]>, AxiosError>(
    queryKey,
    async () => {
      const params = {
        page,
        per_page: itemsPerPage,
        sortField: sort?.field,
        sortDirection: sort?.sort,
        ...filters,
      };
      const resolvedSubTasksData = await ApiService.get(
        `/plants/${plantId}/resolved-subtasks`,
        { params }
      );
      return resolvedSubTasksData.data;
    }
    , {
      enabled: enabled,
    });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch on-site resolved sub-tasks (maintenance tasks) for a plant.
 *
 * @param {string} [plantId] - ID of the plant.
 * @param {string} [pagination] - Pagination enabled/disabled ("0" to disable).
 * @param {number} [page] - Page number.
 * @param {number} [itemsPerPage] - Items per page.
 * @param {string} [resolveDateFrom] - Start date for filtering resolved sub-tasks.
 * @param {string} [resolveDateTo] - End date for filtering resolved sub-tasks.
 * @returns {Object} React Query object with onsite sub-tasks list.
 */
export const useFetchOnsiteSubTasks = (
  plantId?: string,
  pagination?: string,
  page?: number,
  itemsPerPage?: number,
  resolveDateFrom?: string,
  resolveDateTo?: string,
  enabled = true,
  sortField?: string,
  sortDirection?: string
) => {
  const queryKey = ["on-site-subtasks", pagination, page, sortField, sortDirection];
  // TODO: add model
  const { refetch, ...queryResult } = useQuery<IResponse<any[]>, AxiosError>(
    queryKey,
    async () => {
      // configure params
      const params: {
        pagination?: string | undefined;
        page?: number;
        per_page?: number;
        resolve_date_to?: string;
        resolve_date_from?: string;
        sortField?: string;
        sortDirection?: string;
      } = {};

      if (pagination && pagination === "0") {
        params.pagination = pagination;
        params.resolve_date_from = resolveDateFrom;
        params.resolve_date_to = resolveDateTo;
      }
      if (pagination && pagination !== "0") {
        params.page = page;
        params.per_page = itemsPerPage;
        params.resolve_date_from = resolveDateFrom;
        params.resolve_date_to = resolveDateTo;
        params.sortField = sortField;
        params.sortDirection = sortDirection;
      }

      const onsiteSubTasksData = await ApiService.get(
        `/plants/${plantId}/resolved-onsite-vka`,
        { params }
      );
      return onsiteSubTasksData;
    },
    {
      enabled: enabled,
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch laboratory VKA sub-tasks for a plant.
 *
 * @param {string} [plantId] - Plant ID.
 * @param {string} [pagination] - Pagination enabled/disabled.
 * @param {number} [page] - Page number.
 * @param {number} [itemsPerPage] - Items per page.
 * @param {string} [resolveDateFrom] - Start date.
 * @param {string} [resolveDateTo] - End date.
 * @returns {Object} React Query object with laboratory sub-tasks list.
 */
export const useFetchLabSubTasks = (
  plantId?: string,
  pagination?: string,
  page?: number,
  itemsPerPage?: number,
  resolveDateFrom?: string,
  resolveDateTo?: string,
  enabled = true,
  sortField?: string,
  sortDirection?: string
) => {
  const queryKey = ["lab-vka-resolved-subtasks", pagination, page, sortField, sortDirection];
  // TODO: add model
  const { refetch, ...queryResult } = useQuery<IResponse<any[]>, AxiosError>(
    queryKey,
    async () => {
      // configure params
      const params: {
        pagination?: string;
        page?: number;
        per_page?: number;
        resolve_date_to?: string;
        resolve_date_from?: string;
        sortField?: string;
        sortDirection?: string;
      } = {};

      if (pagination && pagination === "0") {
        params.pagination = pagination;
        params.resolve_date_from = resolveDateFrom;
        params.resolve_date_to = resolveDateTo;
      }
      if (pagination && pagination !== "0") {
        params.page = page;
        params.per_page = itemsPerPage;
        params.resolve_date_from = resolveDateFrom;
        params.resolve_date_to = resolveDateTo;
        params.sortField = sortField;
        params.sortDirection = sortDirection;
      }

      const labSubTasksData = await ApiService.get(
        `/plants/${plantId}/resolved-laboratory-vka`,
        { params }
      );
      return labSubTasksData;
    },
    {
      enabled: enabled,
    }
  );
  return { refetch, ...queryResult };
};

/**
 * Hook to fetch microbiological options.
 *
 * @returns {Object} React Query object with microbiological options.
 */
export const useFetchMicrobiologicalOptions = () => {
  const queryKey = ["microbiological-options"];
  const { refetch, ...queryResult } = useQuery<
    MicrobiologicalOptionsModel,
    AxiosError
  >(queryKey, async () => {
    const microbiologicalData = await ApiService.get(
      `/microbiological-options`
    );
    return microbiologicalData?.data;
  }, {
    staleTime: 1000 * 60 * 60 * 1, // 1 hour
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch resolved microbiological VKA sub-tasks.
 *
 * @param {string} [plantId] - Plant ID.
 * @param {string} [pagination] - Pagination enabled/disabled.
 * @param {number} [page] - Page number.
 * @param {number} [itemsPerPage] - Items per page.
 * @param {string} [resolveDateFrom] - Start date.
 * @param {string} [resolveDateTo] - End date.
 * @returns {Object} React Query object with resolved microbiological subtasks and `refetch`.
 */
export const useFetchResolvedMicroTasks = (
  plantId?: string,
  pagination?: string,
  page?: number,
  itemsPerPage?: number,
  resolveDateFrom?: string,
  resolveDateTo?: string,
  enabled = true,
  sortField?: string,
  sortDirection?: string
) => {
  const queryKey = ["resolved-microbiological-vka", pagination, page, sortField, sortDirection];
  // TODO: add model
  const { refetch, ...queryResult } = useQuery<IResponse<any[]>, AxiosError>(
    queryKey,
    async () => {
      // configure params
      const params: {
        pagination?: string;
        page?: number;
        per_page?: number;
        resolve_date_to?: string;
        resolve_date_from?: string;
        sortField?: string;
        sortDirection?: string;
      } = {};

      if (pagination && pagination === "0") {
        params.pagination = pagination;
        params.resolve_date_from = resolveDateFrom;
        params.resolve_date_to = resolveDateTo;
      }
      if (pagination && pagination !== "0") {
        params.page = page;
        params.per_page = itemsPerPage;
        params.resolve_date_from = resolveDateFrom;
        params.resolve_date_to = resolveDateTo;
        params.sortField = sortField;
        params.sortDirection = sortDirection;
      }
      const microSubTasksData = await ApiService.get(
        `/plants/${plantId}/resolved-microbiological-vka`,
        { params }
      );
      return microSubTasksData;
    },
    {
      enabled: enabled,
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch biozide products list.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} React Query object with biozide products and `refetch`.
 */
export const useFetchBiozideProducts = (plantId?: string) => {
  const queryKey = ["plants", plantId, "biozide-products"];
  const { refetch, ...queryResult } = useQuery<any, AxiosError>(
    queryKey,
    async () => {
      const biozideProductslData = await ApiService.get(
        `/plants/${plantId}/biozide-products`
      );
      return biozideProductslData?.data;
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch microbiological threshold for a plant.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} React Query object with microbiological threshold.
 */
export const useFetchMicroThreshold = (plantId?: string) => {
  const queryKey = ["get-micro-threshold", plantId];
  const queryResult = useQuery<any, AxiosError>(
    queryKey,
    async () => {
      const MicroThresholdData = await ApiService.get(
        `/get-micro-threshold/${plantId}`
      );
      return MicroThresholdData?.data;
    }
  );
  return queryResult;
};

/**
 * Hook to fetch microbiological settings.
 *
 * @param {boolean} [enabled=true] - Enable fetching or not.
 * @returns {Object} React Query object with settings and `refetch`.
 */
export const useFetchMicrobiologicalSettings = (enabled: boolean = true) => {
  const queryKey = ["microbiological-settings"];
  const { refetch, ...queryResult } = useQuery<MicroSettings, AxiosError>(
    queryKey,
    async () => {
      const MicrobiologicalSettings = await ApiService.get('/microbiological-settings');
      return MicrobiologicalSettings?.data;
    },
    {
      enabled,
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to create or update microbiological settings.
 *
 * @returns {Object} Mutation object to update microbiological settings.
 */
export const useMicrobiologicalSettings = () => {
  const queryClient = useQueryClient();

  const microbiologicalSettings: MutationFunction<IResponse<any>, FormData> = async (
    formData
  ) => {
    const response = await ApiService.postFormData(
      '/microbiological-settings',
      formData
    );
    return response;
  };
  return useMutation({
    mutationFn: microbiologicalSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microbiological-settings'] })
    }
  });
};

/******************************* Yearly Report *******************************/

/**
 * Hook to fetch sub-tasks for yearly report PDF generation.
 *
 * @param {string} [plantId] - Plant ID.
 * @param {string} [resolveDateFrom] - Start date.
 * @param {string} [resolveDateTo] - End date.
 * @param {boolean} [trigger=false] - Whether to trigger fetching immediately.
 * @returns {Object} React Query object with subtasks for the yearly report.
 */
export const useFetchTasksForYearlyReport = (
  plantId?: string,
  resolveDateFrom?: string,
  resolveDateTo?: string,
  trigger = false
) => {
  const queryKey = ["subtasks-yearly-pdf", resolveDateFrom, resolveDateTo];

  // Use the `enabled` option to control if the query should be triggered
  const { refetch, ...queryResult } = useQuery<IResponse<any[]>, AxiosError>(
    queryKey,
    async () => {
      // configure params
      const params: {
        start_date?: string;
        end_date?: string;
      } = {};

      if (resolveDateFrom && resolveDateFrom) {
        params.start_date = resolveDateFrom;
        params.end_date = resolveDateTo;
      }
      const microSubTasksData = await ApiService.get(
        `/plants/${plantId}/resolved-onsite-vka/validate`,
        { params }
      );
      return microSubTasksData;
    },
    {
      enabled: trigger, // Only fetch when trigger is true
    }
  );

  return { ...queryResult, refetch };
};

/**
 * Hook to create Order Handling IW template.
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object to update Order Handling IW template.
 */
export const useCreateOrderHandlingIWTemplate = (subTaskId?: string) => {
  const queryClient = useQueryClient();

  const createOrderHandlingIWTemplate: MutationFunction<any, FormData> = async (
    formData: FormData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates`,
      formData
    );
    return response.data;
  };

  const mutation = useMutation(createOrderHandlingIWTemplate, {
    onMutate: async () => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);
      return { previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-task", subTaskId]);
    },
  });

  return mutation;
};



/**
 * Hook to update Order Handling IW template.
 *
 * @param {string} [subTaskId] - Sub-task ID.
 * @returns {Object} Mutation object to update Order Handling IW template.
 */
export const useUpdateOrderHandlingIWTemplate = (subTaskId?: string) => {
  const queryClient = useQueryClient();

  const updateOrderHandlingIWTemplate: MutationFunction<any, FormData> = async (
    formData: FormData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates?_method=PATCH`,
      formData
    );
    return response.data;
  };

  const mutation = useMutation(updateOrderHandlingIWTemplate, {
    onMutate: async () => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);
      return { previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-task", subTaskId]);
    },
  });

  return mutation;
};

export const useSaveOrderHandlingIWTemplate = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  const saveOrderHandlingIWTemplate: MutationFunction<any, FormData> = async (
    formData: FormData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/order-handling-iw-templates`,
      formData
    );
    return response.data;
  };

  const mutation = useMutation(saveOrderHandlingIWTemplate, {
    onMutate: async () => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);
      return { previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-task", subTaskId]);
    },
  });
  return mutation;
};

/**
 * Saves a Checkliste Trinkwasser phase without advancing it.
 *
 * Used by the phase "Speichern" button: ticked checkpoints and the checkpoint 4.3 lab
 * data are persisted, but the subtask stays in the same phase.
 */
export const useSaveDrinkingWaterChecklistTemplate = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  const saveDrinkingWaterChecklistTemplate: MutationFunction<any, FormData> = async (
    formData: FormData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/drinking-water-checklist-templates`,
      formData
    );
    return response.data;
  };

  const mutation = useMutation(saveDrinkingWaterChecklistTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-task", subTaskId]);
    },
  });
  return mutation;
};

/**
 * Submits a Checkliste Trinkwasser phase.
 *
 * Goes through the generic template endpoint, which persists the data and moves the
 * subtask on to the next phase (or, from phase 3, cancels it and opens a fresh
 * checklist).
 */
export const useSubmitDrinkingWaterChecklistPhase = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  const submitDrinkingWaterChecklistPhase: MutationFunction<any, FormData> = async (
    formData: FormData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates?_method=PATCH`,
      formData
    );
    return response.data;
  };

  const mutation = useMutation(submitDrinkingWaterChecklistPhase, {
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-task", subTaskId]);
    },
  });
  return mutation;
};

export const useGetTemplateValueChanges = (
  subTaskId?: string,
  perPage = 20,
  page = 1
) => {
  return useQuery<IResponse<any>, AxiosError>(
    ["template-value-changes", subTaskId, perPage, page],
    async () => {
      const params = {
        per_page: perPage,
        page: page
      }
      const response = await ApiService.get(
        `/subtasks/${subTaskId}/template-value-changes`, { params }
      );
      return response.data;
    },
    { enabled: !!subTaskId }
  );
};

export const useCreateTemplateValueChange = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: { comment: string; changed_values: Record<string, any>; display_values?: Record<string, string>; old_display_values?: Record<string, string> }) => {
      const response = await ApiService.post(
        `/subtasks/${subTaskId}/template-value-changes`,
        payload
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sub-task", subTaskId]);
        queryClient.invalidateQueries(["template-value-changes", subTaskId]);
      },
    }
  );
};

export const useUpdatePlantPreStartupTemplate = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  const updatePlantPreStartupTemplate: MutationFunction<any, FormData> = async (formData: FormData) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/templates?_method=PATCH`,
      formData
    );
    return response.data;
  };

  const mutation = useMutation(updatePlantPreStartupTemplate, {
    onMutate: async () => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);
      return { previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-task", subTaskId]);
    },
  });
  return mutation;
};

export const useSavePlantStartupTemplate = (subTaskId?: string) => {
  const queryClient = useQueryClient();
  const savePlantStartupTemplate: MutationFunction<any, FormData> = async (
    formData: FormData
  ) => {
    const response = await ApiService.postFormData(
      `/subtasks/${subTaskId}/plant-startup-templates`,
      formData
    );
    return response.data;
  };

  const mutation = useMutation(savePlantStartupTemplate, {
    onMutate: async () => {
      const previousData = queryClient.getQueryData(["sub-task", subTaskId]);
      return { previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["sub-task", subTaskId]);
    },
  });
  return mutation;
};